import React, { Component, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as R from 'ramda';
import { isEmptyOrNil } from 'core/helpers';
import { RECOMMENDATIONS_GROUPS } from 'core/constants';

import { IconArrowRight } from 'components/icons/icon-arrow-right/icon-arrow-right';
import { RouteLink } from 'components/route-link/route-link';
import { FormattedTag } from 'components/formatted-tag/formatted-tag';
import { prepareSports } from 'helpers/all-inplay';
import { SportIcon } from '../sport-icon/sport-icon';
import { ALL_INPLAY_SPORTS } from '../../../constants';

import './all-inplay-menu-desktop.scss';

export class AllInplayMenuDesktop extends Component {
  static propTypes = {
    locale: PropTypes.string.isRequired,
    inplayGames: PropTypes.arrayOf(PropTypes.shape()),
    liveSportId: PropTypes.number,
    isInplayGamesInProgress: PropTypes.bool.isRequired,
    sports: PropTypes.arrayOf(PropTypes.shape()),
    inplayRecommendations: PropTypes.arrayOf(PropTypes.shape()),
    recommendationsGroup: PropTypes.string.isRequired,
    sendSportClickFeedback: PropTypes.func.isRequired,
  };

  static defaultProps = {
    inplayGames: null,
    liveSportId: null,
    sports: null,
    inplayRecommendations: null,
  };

  carouselRef = createRef();

  prevCarouselWidth;

  resizeWindowSubsription;

  state = {
    isMounted: false,
    isPrevShowed: false,
    isNextShowed: false,
  };

  componentDidMount() {
    const { sports, inplayGames } = this.props;

    if (!isEmptyOrNil(sports) && !R.isNil(inplayGames)) {
      this.setIsMounted(true);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !R.equals(this.props, nextProps) || !R.equals(this.state, nextState);
  }

  componentDidUpdate(prevProps, prevState) {
    const { sports, inplayGames } = this.props;
    const { sports: prevSports, inplayGames: prevInplayGames } = prevProps;
    const { isMounted } = this.state;
    const { isMounted: prevIsMounted } = prevState;

    if ((isEmptyOrNil(prevSports) || R.isNil(prevInplayGames)) && !isEmptyOrNil(sports) && !R.isNil(inplayGames)) {
      this.setIsMounted(true);
    }

    if (!prevIsMounted && isMounted) {
      this.prevCarouselWidth = this.carouselRef.current.getBoundingClientRect().width;
      this.resizeWindowSubsription = fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .subscribe(this.onWindowResize);
      this.scrollToSport();
    }
  }

  componentWillUnmount() {
    if (this.resizeWindowSubsription) {
      this.resizeWindowSubsription.unsubscribe();
    }
  }

  setIsMounted = isMounted => this.setState({ isMounted });

  setArrowsVisibility = () => {
    const carousel = this.carouselRef && this.carouselRef.current;

    if (carousel) {
      let isPrevShowed = false;
      let isNextShowed = false;

      if (carousel.style.transfrom !== 'translate3d(0px, 0px, 0px)') {
        isPrevShowed = true;
      }

      if (this.getCarouselItemsWidth() > carousel.getBoundingClientRect().width) {
        isNextShowed = true;
      }

      this.setState({ isPrevShowed, isNextShowed });
    }
  };

  onPrevClick = () => {
    const { isNextShowed } = this.state;
    const carousel = this.carouselRef.current;
    const maxPosition = 0;
    const prevPosition = Number(carousel.style.transform.match(/\((.*?)px/)[1]);
    let position = prevPosition + carousel.getBoundingClientRect().width / 2;

    if (position >= maxPosition) {
      position = maxPosition;
      this.setState({ isPrevShowed: false });
    }

    if (!isNextShowed) {
      this.setState({ isNextShowed: true });
    }

    carousel.style.transform = `translate3d(${position}px, 0, 0)`;
  }

  onNextClick = () => {
    const { isPrevShowed } = this.state;
    const carousel = this.carouselRef.current;
    const carouselWidth = carousel.getBoundingClientRect().width;
    const maxPosition = (this.getCarouselItemsWidth() - carouselWidth) * -1;
    const prevPosition = Number(carousel.style.transform.match(/\((.*?)px/)[1]);
    let position = prevPosition - carouselWidth / 2;

    if (position <= maxPosition) {
      position = maxPosition;
      this.setState({ isNextShowed: false });
    }

    if (!isPrevShowed) {
      this.setState({ isPrevShowed: true });
    }

    carousel.style.transform = `translate3d(${position}px, 0, 0)`;
  }

  onWindowResize = () => {
    const { isPrevShowed, isNextShowed } = this.state;
    const carousel = this.carouselRef && this.carouselRef.current;

    if (carousel) {
      const carouselWidth = carousel.getBoundingClientRect().width;
      const prevPosition = Number(carousel.style.transform.match(/\((.*?)px/)[1]);

      if (this.prevCarouselWidth < carouselWidth) { // size +
        let maxPosition = (this.getCarouselItemsWidth() - carouselWidth) * -1;

        if (maxPosition > 0) {
          maxPosition = 0;

          if (isNextShowed) {
            this.setState({ isNextShowed: false });
          }

          if (isPrevShowed) {
            this.setState({ isPrevShowed: false });
          }
        }

        if (prevPosition <= maxPosition) {
          carousel.style.transform = `translate3d(${maxPosition}px, 0, 0)`;
        }
      } else { // size -
        const maxPosition = (this.getCarouselItemsWidth() - carouselWidth) * -1;

        if (!isNextShowed && maxPosition < prevPosition) {
          this.setState({ isNextShowed: true });
        }
      }

      this.prevCarouselWidth = carouselWidth;
    }
  }

  scrollToSport = () => {
    const { liveSportId } = this.props;
    const carousel = this.carouselRef && this.carouselRef.current;
    let isPrevShowed = false;
    let isNextShowed = false;

    if (carousel) {
      const carouselWidth = carousel.getBoundingClientRect().width;
      const itemsWidth = this.getCarouselItemsWidth();
      const foundSport = carousel.querySelector(`[data-sport-id='${liveSportId}']`);

      if (foundSport) {
        const { offsetLeft, offsetWidth } = foundSport;
        const offset = Math.min(offsetLeft - 24, itemsWidth - carouselWidth); // 24px - arrow width

        if (offsetLeft + offsetWidth > carouselWidth) {
          carousel.style.transform = `translate3d(-${offset}px, 0, 0)`;
          isPrevShowed = true;
          isNextShowed = Math.floor(itemsWidth - (offsetWidth + offsetLeft)) > carouselWidth;
        } else if (itemsWidth > carouselWidth) {
          isNextShowed = true;
        }
      } else if (itemsWidth > carouselWidth) {
        isNextShowed = true;
      }

      this.setState({ isPrevShowed, isNextShowed });
    }
  };

  getCarouselItemsWidth = () => {
    const carousel = this.carouselRef && this.carouselRef.current;
    let itemsWidth = 0;

    if (carousel) {
      for (let i = 0; i < carousel.children.length; i += 1) {
        itemsWidth += carousel.children[i].getBoundingClientRect().width;
      }
    }

    return itemsWidth;
  };

  onClick = (e) => {
    const { dataset: { sportId } } = e.currentTarget;
    const { sendSportClickFeedback } = this.props;

    sendSportClickFeedback(sportId);
  };

  render() {
    const {
      locale,
      liveSportId,
      sports,
      inplayGames,
      isInplayGamesInProgress,
      inplayRecommendations,
      recommendationsGroup,
    } = this.props;
    const { isMounted, isPrevShowed, isNextShowed } = this.state;
    const preparedSports = prepareSports(inplayGames, sports);
    const isDefaultRecommendations = recommendationsGroup === RECOMMENDATIONS_GROUPS.DEFAULT;

    return (
      <div className="all-in-play-desktop bg-main-2 rounded overflow-hidden position-relative">
        {isMounted && (
          <Fragment>
            <div
              ref={this.carouselRef}
              className={classNames('all-in-play-desktop-carousel d-flex', {
                invisible: isInplayGamesInProgress,
              })}
              style={{ transform: 'translate3d(0px, 0px, 0px)' }}
            >
              {(isDefaultRecommendations || !isEmptyOrNil(inplayRecommendations)) && (
                <RouteLink
                  locale={locale}
                  to="/live"
                  className={classNames('all-in-play-desktop-item px-2 flex-shrink-0 d-flex flex-column justify-content-center align-items-center text-center bg-main-2', {
                    'text-extra-2': liveSportId,
                  })}
                >
                  <SportIcon sportId={ALL_INPLAY_SPORTS} isActive={!liveSportId} locale={locale} />
                  <FormattedTag id={`general.${isDefaultRecommendations ? 'all-sports' : 'for-you'}`} className="mt-0_5 label" />
                </RouteLink>
              )}

              {preparedSports.map(({ sportId, sportName }) => (
                <RouteLink
                  locale={locale}
                  to={`/live/${sportId}`}
                  className={classNames('all-in-play-desktop-item px-2 flex-shrink-0 d-flex flex-column justify-content-center align-items-center text-center bg-main-2', {
                    'text-extra-2': sportId !== liveSportId,
                  })}
                  data-sport-id={sportId}
                  onClick={this.onClick}
                >
                  <SportIcon sportId={sportId} isActive={sportId === liveSportId} locale={locale} />
                  <span className="mt-0_5 label">{sportName}</span>
                </RouteLink>
              ))}
            </div>

            {isPrevShowed && (
              <div
                role="button"
                tabIndex="0"
                onClick={this.onPrevClick}
                onKeyPress={this.onPrevClick}
                className={classNames('all-in-play-desktop-prev d-flex align-items-center justify-content-center position-absolute bg-main-3', {
                  invisible: isInplayGamesInProgress,
                })}
              >
                <IconArrowRight />
              </div>
            )}

            {isNextShowed && (
              <div
                role="button"
                tabIndex="0"
                onClick={this.onNextClick}
                onKeyPress={this.onNextClick}
                className={classNames('all-in-play-desktop-next d-flex align-items-center justify-content-center position-absolute bg-main-3', {
                  invisible: isInplayGamesInProgress,
                })}
              >
                <IconArrowRight />
              </div>
            )}
          </Fragment>
        )}
      </div>
    );
  }
}
